import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { type AdminRegionalEvent } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { StatusPill } from '@/components/cccd/StatusPill';
import Alert from '@/components/ui/alert/Alert';
import DataTable, { type DataTableColumn } from '@/components/ui/table/DataTable';
import { useServerTable } from '@/hooks/useServerTable';
import { EyeIcon, CopyIcon } from '@/icons';
import { liveResultsUrl, launchLiveUrl } from '@/components/cccd/ProjectionButtons';

const MonitorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
);

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  return `${new Date(start).toLocaleDateString('es-CO', opts)} – ${new Date(end).toLocaleDateString('es-CO', opts)}`;
}

export default function RegionalsListPage() {
  const navigate = useNavigate();  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'active' | 'closed'>('');

  const {
    rows: regionals,
    total: regionalsTotal,
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
  } = useServerTable<AdminRegionalEvent>({
    endpoint: '/cccd/admin/regionals',
    defaultPageSize: 20,
    defaultSort: { column: 'eventStartDate', dir: 'desc' },
    // El filtro de estado viaja al servidor: el total y la paginación son reales con cualquier cantidad de regionales.
    params: { search: search || undefined, isActive: activeFilter === 'active' ? 'true' : activeFilter === 'closed' ? 'false' : undefined },
    extractRows: (data) => data.regionals ?? [],
    extractTotal: (data) => data.total ?? 0,
  });
  // Totales globales de todos los regionales (no dependen de la página, la búsqueda ni el filtro).
  const summary = (payload as { summary?: { total: number; active: number; closed: number; teams: number } } | undefined)?.summary;

  const columns: DataTableColumn<AdminRegionalEvent>[] = [
    {
      key: 'name',
      label: 'Regional',
      sortable: true,
      render: (r) => (
        <Link to={`/cccd/regionals/${r.id}`} className="block hover:underline">
          <p className="font-medium text-gray-900 dark:text-white">{r.name}</p>
          <p className="text-xs text-gray-500">{r.slug}</p>
        </Link>
      ),
    },
    {
      key: 'region',
      label: 'Región',
      render: (r) => <span className="text-sm text-gray-600 dark:text-gray-400">{r.region}</span>,
    },
    {
      key: 'eventStartDate',
      label: 'Fechas del evento',
      sortable: true,
      render: (r) => (
        <span className="text-xs text-gray-500">{formatDateRange(r.eventStartDate, r.eventEndDate)}</span>
      ),
    },
    {
      key: 'teams',
      label: 'Equipos',
      align: 'center',
      render: (r) => <span className="text-sm text-gray-600 dark:text-gray-400">{r._count.teams}</span>,
    },
    {
      key: 'isActive',
      label: 'Estado',
      align: 'center',
      render: (r) => <StatusPill tone={r.isActive ? 'success' : 'gray'} label={r.isActive ? 'Activo' : 'Inactivo'} />,
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              { label: 'Ver detalle', icon: <EyeIcon />, to: `/cccd/regionals/${r.id}` },
              { label: 'Proyectar Resultados Generales', icon: <MonitorIcon />, onClick: () => window.open(liveResultsUrl(r.id), '_blank', 'noopener,noreferrer') },
              { label: 'Proyectar Lanzamientos', icon: <MonitorIcon />, onClick: () => window.open(launchLiveUrl(r.id), '_blank', 'noopener,noreferrer') },
              { label: 'Duplicar', icon: <CopyIcon />, to: `/cccd/regionals/new?fromId=${r.id}`, requires: 'cccd:events-regional:edit' },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Regionales']}
      title="Eventos Regionales"
      subtitle="Concursos regionales CCCD, sus precios y calendario de inscripciones"
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Eventos', to: '/cccd/events' },
        { label: 'Regionales', to: '/cccd/regionals', active: true },
      ]}
      metrics={[
        { label: 'Regionales', value: summary ? String(summary.total) : '—' },
        { label: 'Activos', value: summary ? String(summary.active) : '—', tone: 'good', hint: 'Abiertos a inscripción' },
        { label: 'Cerrados', value: summary ? String(summary.closed) : '—' },
        { label: 'Equipos inscritos', value: summary ? String(summary.teams) : '—', hint: 'En todas las sedes regionales' },
      ]}
      toolbar={{
        search: { value: search, onChange: setSearch, placeholder: 'Buscar regional...' },
        filters: [
          {
            type: 'select',
            key: 'active',
            label: 'Estado',
            value: activeFilter,
            onChange: (v) => setActiveFilter(v as '' | 'active' | 'closed'),
            options: [
              { label: 'Todos', value: '' },
              { label: 'Activo', value: 'active' },
              { label: 'Cerrado', value: 'closed' },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate('/cccd/regionals/new'),
        createLabel: '+ Nuevo regional',
        requires: 'cccd:events-regional:edit',
      }}
    >
      {error && <div className="mb-4"><Alert variant="error" title="No se pudieron cargar los regionales" message={error} /></div>}

      <DataTable
        columns={columns}
        rows={regionals}
        rowKey={(r) => r.id}
        loading={loading}
        emptyMessage="No hay regionales registrados."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={regionalsTotal}
        onPageChange={setPage}
      />    </ModuleLayout>
  );
}
